import api from './api';

/**
 * TEM-989 — one compact heritage fact carried from a Tempest DJ set upload
 * (the "Radio lineage leg"). Stored verbatim on `Stream.metadata.lineage` and
 * rendered read-only by `LineageHeritage`. Mirrors the backend `LineageEntry`
 * (`apps/radio/backend/src/models/stream.ts`) and the Rust `LineageLeg`.
 */
export interface LineageEntry {
  album?: string;
  edge_type: string;
  target_name: string;
  source: string;
  confidence?: number;
  narrative_text?: string;
  /** TEM-1591 — the leg's tier token (`'credited'` on everything DJ publishes
   *  since); absent on a narrative-only leg and on sets published before. */
  tier?: string;
  /** TEM-1591 follow-up — a credited leg's role in the album panel's words
   *  ("Composer", "on bass"), shown in place of "Credited"; absent when the
   *  credit states none and on sets published before. */
  role?: string;
}

export interface Stream {
  streamId: string;
  name: string;
  description: string;
  genre: string;
  coverImage?: string;
  audioUrl: string;
  status: 'active' | 'inactive' | 'scheduled';
  bitrate?: number;
  format?: 'mp3' | 'm4a' | 'aac' | 'ogg' | 'opus' | 'wav' | 'flac' | 'alac' | 'aiff' | 'webm' | 'wma';
  waveformData?: number[]; // Pre-generated waveform amplitudes for visualization
  playCount?: number; // Total number of plays
  viewCount?: number; // Total number of views/impressions
  createdAt: number;
  updatedAt: number;
  createdBy?: string;
  // Uploader info
  uploaderUsername?: string;
  uploaderDisplayName?: string;
  uploaderProfilePictureUrl?: string;
  metadata?: {
    artist?: string;
    album?: string;
    year?: number;
    source?: string;
    duration?: number;
    bpm_min?: number;
    bpm_max?: number;
    /**
     * TEM-1141 — a set's tracklist has TWO producers with different field names.
     * Read the start offset with `trackStartMs()` (StreamDetail.tsx), never directly.
     *
     *  - DJ sets (Radio's own AudD recognition path) emit snake_case:
     *      { timestamp_ms, deck_id, title, artist, bpm, key }
     *  - Assembled sets published by Record emit camelCase, because
     *      `AssembledTrack` carries `#[serde(rename_all = "camelCase")]`
     *      (`apps/record/src-tauri/src/set_assemble.rs`):
     *      { position, title, artist, album, startMs, durationMs }
     *
     * Only `title` is guaranteed by both. Everything else is optional.
     */
    tracklist?: Array<{
      /** DJ-set producer. Absent on Record-assembled sets. */
      timestamp_ms?: number;
      /** Record-assembled producer. Absent on DJ sets. */
      startMs?: number;
      deck_id?: number;
      title: string;
      artist?: string;
      album?: string;
      bpm?: number;
      key?: string;
      position?: number;
      durationMs?: number;
    }>;
    /** TEM-989 — per-album heritage from the DJ set's collection tracks. */
    lineage?: LineageEntry[];
    [key: string]: any;
  };
}

export interface CreateStreamInput {
  name: string;
  description: string;
  genre: string;
  coverImage?: string;
  audioUrl: string;
  bitrate?: number;
  format?: 'mp3' | 'm4a' | 'aac' | 'ogg' | 'opus' | 'wav' | 'flac' | 'alac' | 'aiff' | 'webm' | 'wma';
  waveformData?: number[];
  metadata?: Record<string, any>;
}

export interface UpdateStreamInput {
  name?: string;
  description?: string;
  genre?: string;
  coverImage?: string;
  audioUrl?: string;
  status?: 'active' | 'inactive' | 'scheduled';
  bitrate?: number;
  format?: 'mp3' | 'm4a' | 'aac' | 'ogg' | 'opus' | 'wav' | 'flac' | 'alac' | 'aiff' | 'webm' | 'wma';
  waveformData?: number[];
  metadata?: Record<string, any>;
}

export const streamService = {
  async getAllStreams(status?: string): Promise<Stream[]> {
    const url = status ? `/streams?status=${status}` : '/streams';
    return api.get<Stream[]>(url);
  },

  async getTrendingStreams(limit: number = 20): Promise<Stream[]> {
    return api.get<Stream[]>(`/streams/trending?limit=${limit}`);
  },

  async getMostPlayedStreams(limit: number = 20): Promise<Stream[]> {
    return api.get<Stream[]>(`/streams/most-played?limit=${limit}`);
  },

  async getStreamById(streamId: string): Promise<Stream> {
    return api.get<Stream>(`/streams/${streamId}`);
  },

  async incrementPlayCount(streamId: string): Promise<{ success: boolean }> {
    return api.post<{ success: boolean }>(`/streams/${streamId}/play`, {});
  },

  async createStream(input: CreateStreamInput): Promise<Stream> {
    return api.post<Stream>('/streams', input);
  },

  async updateStream(streamId: string, input: UpdateStreamInput): Promise<Stream> {
    return api.put<Stream>(`/streams/${streamId}`, input);
  },

  async deleteStream(streamId: string): Promise<{ deleted: boolean }> {
    return api.delete<{ deleted: boolean }>(`/streams/${streamId}`);
  },

  // Cover image upload methods
  async getCoverImageUploadUrl(
    streamId: string,
    fileName: string,
    fileType: string,
    fileSize: number
  ): Promise<{ uploadUrl: string; key: string; expiresIn: number }> {
    return api.post(`/streams/${streamId}/cover-image/upload-url`, {
      fileName,
      fileType,
      fileSize
    });
  },

  async validateCoverImage(
    streamId: string,
    key: string
  ): Promise<{ isValid: boolean; coverImageUrl: string }> {
    return api.post(`/streams/${streamId}/cover-image/validate`, { key });
  },

  async deleteCoverImage(streamId: string): Promise<{ message: string }> {
    return api.delete(`/streams/${streamId}/cover-image`);
  },

  // Helper to upload cover image (full flow)
  async uploadCoverImage(streamId: string, file: File): Promise<string> {
    // Step 1: Get presigned URL
    const { uploadUrl, key } = await this.getCoverImageUploadUrl(
      streamId,
      file.name,
      file.type,
      file.size
    );

    // Step 2: Upload to S3
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload image to S3');
    }

    // Step 3: Validate and get CloudFront URL
    const { coverImageUrl } = await this.validateCoverImage(streamId, key);

    return coverImageUrl;
  }
};

export default streamService;
